import { createDefaultZoneSection } from './default-sections.js'
import { areaFor, type DynamicArea } from './context.js'
import { MAX_COLUMNS_PER_ROW, MAX_ROWS, isSectionFull, type AreaLayout, type Section, type Widget, type WidgetCopyOptions } from './editor-state.js'

export interface CopyableView {
  sections: Section[]
  areaLayout: AreaLayout
}

export interface WidgetCopyIds {
  widgetId: (type: Widget['type']) => string
  zoneId: (area: DynamicArea) => string
}

export interface WidgetCopyResult {
  view: CopyableView
  added: number
  skippedExisting: number
  unmatched: number
}

const DYNAMIC_AREAS: DynamicArea[] = ['productInfo', 'orderActions']

export const countWidgets = (sections: Section[]): number =>
  sections.reduce((total, section) => total + section.widgets.length, 0)

const widgetTypesIn = (sections: Section[]): Set<Widget['type']> =>
  new Set(sections.flatMap(section => section.widgets.map(widget => widget.type)))

export const sharedWidgetTypeCount = (source: Section[], target: Section[]): number => {
  const targetTypes = widgetTypesIn(target)
  return [...widgetTypesIn(source)].filter(type => targetTypes.has(type)).length
}

const zonePosition = (areaLayout: AreaLayout, area: DynamicArea, sectionId: string): { row: number; column: number } | null => {
  for (const [row, zoneIds] of areaLayout[area].entries()) {
    const column = zoneIds.indexOf(sectionId)
    if (column !== -1) return { row, column }
  }
  return null
}

const zoneStyleOf = (section: Section | undefined): Partial<Section> => ({
  ...(section?.direction && { direction: section.direction }),
  ...(section?.justifyContent && { justifyContent: section.justifyContent }),
  ...(section?.alignItems && { alignItems: section.alignItems }),
  ...(section?.wrap && { wrap: section.wrap }),
  ...(section?.gap && { gap: section.gap }),
})

export const copyWidgets = (source: CopyableView, target: CopyableView, options: WidgetCopyOptions, ids: WidgetCopyIds): WidgetCopyResult => {
  const targetTypes = widgetTypesIn(target.sections)
  const sections = target.sections.map(section => ({ ...section, widgets: [...section.widgets] }))
  const areaLayout: AreaLayout = {
    productInfo: target.areaLayout.productInfo.map(row => [...row]),
    orderActions: target.areaLayout.orderActions.map(row => [...row]),
  }
  let added = 0
  let skippedExisting = 0
  let unmatched = 0

  const sourceZoneAt = (area: DynamicArea, row: number, column: number): Section | undefined => {
    const sectionId = source.areaLayout[area][row]?.[column]
    return source.sections.find(section => section.id === sectionId)
  }

  const createZone = (area: DynamicArea, row: number, column: number): string => {
    let id = ids.zoneId(area)
    while (sections.some(section => section.id === id)) id = ids.zoneId(area)
    const dropAreaType = area === 'productInfo' ? 'product-info' : 'product-footer'
    const zone = createDefaultZoneSection(id, `Zone ${areaLayout[area].flat().length + 1}`, dropAreaType)
    sections.push({ ...zone, ...zoneStyleOf(sourceZoneAt(area, row, column)) })
    return id
  }

  const ensureZone = (area: DynamicArea, row: number, column: number): string | null => {
    const rows = areaLayout[area]
    while (rows.length <= row && rows.length < MAX_ROWS) {
      rows.push([createZone(area, rows.length, 0)])
    }
    const targetRow = rows[row]
    if (!targetRow) return null
    while (targetRow.length <= column && targetRow.length < MAX_COLUMNS_PER_ROW) {
      targetRow.push(createZone(area, row, targetRow.length))
    }
    return targetRow[column] ?? null
  }

  const targetSectionFor = (sourceSection: Section): Section | undefined => {
    const area = areaFor(sourceSection.dropAreaType)
    if (!area) return sections.find(section => section.id === sourceSection.id)
    const position = zonePosition(source.areaLayout, area, sourceSection.id)
    if (!position) return undefined
    const zoneId = ensureZone(area, position.row, position.column)
    return sections.find(section => section.id === zoneId)
  }

  const orderedSourceSections = [
    ...source.sections.filter(section => !areaFor(section.dropAreaType)),
    ...DYNAMIC_AREAS.flatMap(area => source.areaLayout[area].flat()
      .map(sectionId => source.sections.find(section => section.id === sectionId))
      .filter((section): section is Section => !!section)),
  ]

  orderedSourceSections.forEach(sourceSection => {
    const widgets = [...sourceSection.widgets].sort((a, b) => a.sequence - b.sequence)
    const toCopy = widgets.filter(widget => !(options.skipExistingTypes && targetTypes.has(widget.type)))
    skippedExisting += widgets.length - toCopy.length
    if (!toCopy.length) return

    const targetSection = targetSectionFor(sourceSection)
    if (!targetSection) {
      unmatched += toCopy.length
      return
    }

    toCopy.forEach(widget => {
      if (isSectionFull(targetSection)) {
        unmatched++
        return
      }
      const lastSequence = Math.max(0, ...targetSection.widgets.map(w => w.sequence))
      targetSection.widgets.push({
        ...structuredClone(widget),
        id: ids.widgetId(widget.type),
        section: targetSection.id,
        sequence: lastSequence + 1,
      })
      added++
    })
  })

  return { view: { sections, areaLayout }, added, skippedExisting, unmatched }
}

export const previewWidgetCopy = (source: CopyableView, target: CopyableView, options: WidgetCopyOptions): WidgetCopyResult => {
  let counter = 0
  return copyWidgets(source, target, options, {
    widgetId: type => `${type}-preview-${counter++}`,
    zoneId: area => `${area}-preview-zone-${counter++}`,
  })
}
